import { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Users, Building2, ExternalLink,
  Crown, AlertTriangle, Target, Flag, Loader2, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import SectionActions from '@/components/SectionActions';
import FilerHoldingsModal from '@/components/FilerHoldingsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// InsiderActivityTab — fully built but intentionally NOT yet wired
// into StockModal's tab registry. The data layer is complete so
// when the paywall ships we can mount this tab with a single line
// in StockModal.js. Reads /api/insider-activity/{ticker}.
//
// Two stacked sections:
//   1. Cluster-detection header (3+ insiders all buying = bullish tag)
//   2. Recent Insider Trades table (Form 4, last 90 days)
//   3. Top Institutional Holders table (13-F, latest quarter)

// Massive SEC Form-4 transaction codes — only the ones we render
// meaningfully. The rest fall through to a neutral "Other" tone.
const TRANSACTION_CODES = {
  P: { label: 'Purchase',        tone: 'emerald' },
  S: { label: 'Sale',            tone: 'rose' },
  A: { label: 'Grant / Award',   tone: 'slate' },
  M: { label: 'Option Exercise', tone: 'amber' },
  D: { label: 'Disposition',     tone: 'rose' },
  G: { label: 'Gift',            tone: 'slate' },
  F: { label: 'Tax Withholding', tone: 'slate' },
  C: { label: 'Conversion',      tone: 'amber' },
  X: { label: 'Option Exercise', tone: 'amber' },
};

const fmtMoney = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
};
const fmtShares = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString();
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
  } catch { return iso; }
};

function roleLabel(row) {
  const parts = [];
  if (row.is_officer && row.officer_title) parts.push(row.officer_title);
  else if (row.is_officer) parts.push('Officer');
  if (row.is_director) parts.push('Director');
  if (row.is_ten_percent_owner) parts.push('10%+ Owner');
  return parts.join(' · ') || 'Insider';
}

export default function InsiderActivityTab({ ticker, user = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-section refs so the Copy / Print / PDF buttons in each
  // section header can capture exactly that section's content
  // (table + caption) without bleeding the other section in.
  const form4Ref = useRef(null);
  const f13Ref = useRef(null);
  // Round 97l (Feb 2026) — Form 3 baseline insider positions ref so
  // the section's Copy/Print/PDF buttons can scope to just the
  // baseline table without bleeding the Form 4 timeline below it.
  const form3Ref = useRef(null);
  // Round 92o++++++++N++++++++++ (Feb 2026) — filer drill-down. Click
  // any filer-name cell in the 13-F table to open a modal showing
  // that manager's top 25 positions (latest quarter, ranked by mkt
  // value). One state pair lifted up so a single modal instance can
  // be reused across all rows — keeps mount cost low.
  const [filerDrill, setFilerDrill] = useState({ open: false, cik: null, name: null });
  const openFiler = (cik, name) => {
    if (!cik) return;
    setFilerDrill({ open: true, cik, name });
  };
  const closeFiler = () => setFilerDrill({ open: false, cik: null, name: null });

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/insider-activity/${ticker}`, { withCredentials: true })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ticker]);

  const form4Rows = data?.form4?.rows || [];
  const summary = data?.form4?.summary || {};
  const f13Rows = data?.thirteen_f?.rows || [];
  const familyTickers = data?.thirteen_f?.family_tickers || null;
  // Round 97l (Feb 2026) — Form 3 = initial Section-16 baseline
  // position when a NEW insider joins (director hired, officer
  // promoted, holder crosses 10%). Already sorted by shares_owned
  // desc on the backend.
  const form3Rows = data?.form3?.rows || [];

  // Only the directional trades (P/S) appear in the main timeline so
  // option grants and tax withholdings don't drown out real signal.
  const directionalRows = useMemo(() => form4Rows.filter((r) => {
    const code = (r.transaction_code || '').toUpperCase();
    return code === 'P' || code === 'S';
  }), [form4Rows]);

  if (loading) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="insider-activity-loading">
        {/* Feb 2026 — expanded loading state per user spec. The SEC
            round-trip takes 6-15s on a cold fetch; give readers a
            spinner + an explainer so the wait doesn't feel like the
            tab is broken. Reading the "why so long?" copy naturally
            burns 4-6s, which fills the gap. */}
        <div className="flex items-center justify-center mb-4">
          <Loader2 className="w-10 h-10 text-cyan-600 dark:text-cyan-400 animate-spin" aria-hidden="true" />
        </div>
        <p className="text-center text-sm font-semibold text-foreground mb-1">
          Loading insider &amp; institutional activity for <span className="font-mono">{ticker}</span>…
        </p>
        <p className="text-center text-xs text-muted-foreground mb-6">
          Please wait — this may take up to 15 seconds.
        </p>
        <div className="rounded-lg border border-cyan-500/30 bg-cyan-50/40 dark:bg-cyan-950/20 p-4 text-xs text-foreground/85 leading-relaxed space-y-2">
          <p className="font-bold text-cyan-800 dark:text-cyan-300 uppercase tracking-wider text-[10px]">
            Why does this take longer than the other tabs?
          </p>
          <p>
            This tab pulls <strong>directly from the SEC&apos;s EDGAR
            filing system</strong> — the same public database analysts
            and regulators use — rather than a pre-computed cache.
            EDGAR is the authoritative source, so accuracy trumps
            speed here.
          </p>
          <p>
            For every ticker we fetch <strong>Form 3</strong>{' '}
            (initial insider baselines), <strong>Form 4</strong>{' '}
            (open-market buys and sells over the last 90 days),
            <strong> Form 13D/G/F</strong> (institutional ownership
            filings from hedge funds, mutual funds, and activists),
            and <strong>10-K / 10-Q</strong> ownership schedules.
            Each filing is downloaded as raw XML, parsed line by
            line, cross-referenced against the CIK-to-ticker map, and
            deduplicated so amended filings don&apos;t double-count.
          </p>
          <p>
            The result is stitched together on the fly into the
            timeline you see below: Section-16 baseline positions,
            recent open-market trades, and the top institutional
            holders — all pulled fresh, all traceable back to the
            original SEC filing via the doc-link icons on each row.
          </p>
          <p className="text-muted-foreground italic">
            Once the tab has loaded once, it stays cached for the
            rest of your session — subsequent opens on the same
            ticker are near-instant.
          </p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6" data-testid="insider-activity-error">
        <div className="flex items-start gap-2 p-3 rounded-lg border border-rose-300/60 dark:border-rose-700/40 bg-rose-50/60 dark:bg-rose-950/30">
          <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-300 mt-0.5 shrink-0" />
          <p className="text-xs text-rose-800 dark:text-rose-200">
            Couldn&apos;t load insider data: {error}
          </p>
        </div>
      </div>
    );
  }
  if (!form4Rows.length && !f13Rows.length && !form3Rows.length) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground" data-testid="insider-activity-empty">
        No insider or institutional filings on record for {ticker} in the last 90 days.
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5" data-testid="insider-activity-tab">

      {/* Cluster-detection summary strip */}
      <ClusterSummaryStrip summary={summary} />

      {/* Initial Insider Positions (Form 3) — baseline holdings at the
          moment a new insider first becomes subject to Section 16.
          Pairs with the Form 4 timeline below: Form 3 is the "starting
          dot" and Form 4 is every move thereafter. Section is hidden
          when no Form 3 filings exist (common for very old issuers
          where every insider joined before Massive's coverage window). */}
      {form3Rows.length > 0 && (
        <Form3Section
          rows={form3Rows}
          ticker={ticker}
          user={user}
          sectionRef={form3Ref}
        />
      )}

      {/* Recent Insider Trades (Form-4) */}
      <section data-testid="insider-form4-section">
        <header className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
          <h3 className="text-sm font-bold flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
            Recent Insider Trades
          </h3>
          <div className="flex items-baseline gap-3 flex-wrap">
            <SectionActions
              targetRef={form4Ref}
              elementId={`insider-form4-${ticker}`}
              title={`${ticker} — Recent Insider Trades`}
              filename={`${ticker}-insider-trades-form4`}
              user={user}
              testid="insider-form4-actions"
            />
            <span className="text-[10px] text-muted-foreground">
              SEC Form 4 · last 90 days
            </span>
          </div>
        </header>
        <div ref={form4Ref} id={`insider-form4-${ticker}`} data-pdf-root>
        {directionalRows.length === 0 ? (
          <p className="text-[12px] text-muted-foreground py-3 border border-dashed border-border rounded-md px-3">
            No open-market buys or sells in the last 90 days. (Option grants &amp; tax withholdings hidden by default.)
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-full text-[11px]" data-testid="insider-form4-table">
              <thead className="bg-muted/40">
                <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-2 py-1.5 text-left">Date</th>
                  <th className="px-2 py-1.5 text-left">Insider</th>
                  <th className="px-2 py-1.5 text-left">Action</th>
                  <th className="px-2 py-1.5 text-right">Shares</th>
                  <th className="px-2 py-1.5 text-right">Price</th>
                  <th className="px-2 py-1.5 text-right">Value</th>
                  <th className="px-2 py-1.5 text-center">10b5-1</th>
                  <th className="px-2 py-1.5 text-center">Doc</th>
                </tr>
              </thead>
              <tbody>
                {directionalRows.slice(0, 25).map((r, idx) => (
                  <Form4Row key={idx} row={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </section>

      {/* Top Institutional Holders (13-F) */}
      <section data-testid="insider-13f-section">
        <header className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
          <h3 className="text-sm font-bold flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
            Top Institutional Holders
          </h3>
          <div className="flex items-baseline gap-3 flex-wrap">
            <SectionActions
              targetRef={f13Ref}
              elementId={`insider-13f-${ticker}`}
              title={`${ticker} — Top Institutional Holders`}
              filename={`${ticker}-institutional-holders-13f`}
              user={user}
              testid="insider-13f-actions"
            />
            <span className="text-[10px] text-muted-foreground">
              SEC Form 13-F · latest quarter
            </span>
          </div>
        </header>

        {/* Round P129 (Feb 2026) — Family view banner. Fires for
            dual-class equities (GOOGL/GOOG, BRK.A/BRK.B, BF.A/BF.B,
            FOX/FOXA, NWS/NWSA). Explains that the roster below
            merges both share classes so users see the whole
            institutional consensus regardless of which class they
            clicked into. */}
        {familyTickers && familyTickers.length > 1 && (
          <div
            className="mb-3 rounded-md border border-violet-300/60 dark:border-violet-700/40 bg-violet-50/60 dark:bg-violet-950/25 px-3 py-1.5 text-[11px] text-violet-900 dark:text-violet-200"
            data-testid="insider-13f-family-notice"
          >
            <strong>Dual-class family view:</strong> holdings below combine{' '}
            <span className="font-mono">{familyTickers.join(' + ')}</span>{' '}
            so Berkshire, Pershing and the rest of the smart-money
            crowd surface even when they only report the sibling
            class.
          </div>
        )}

        {/* Smart-Money Crossover badge (Round 92o++++++++N++++++++++,
            Feb 2026) — fires when 3+ of our hand-curated famous
            institutions all hold the same ticker. Lets users spot
            consensus picks at a glance without scrolling the whole
            13-F table. */}
        {data?.thirteen_f?.smart_money && (
          <SmartMoneyBadge
            summary={data.thirteen_f.smart_money}
            onOpenFiler={openFiler}
          />
        )}
        <div ref={f13Ref} id={`insider-13f-${ticker}`} data-pdf-root>
        {f13Rows.length === 0 ? (
          <p className="text-[12px] text-muted-foreground py-3 border border-dashed border-border rounded-md px-3">
            No 13-F holdings found via issuer-name match. (Per-ticker 13-F lookup requires a CUSIP cross-reference table — coming soon.)
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-full text-[11px]" data-testid="insider-13f-table">
              <thead className="bg-muted/40">
                <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-2 py-1.5 text-left">Filer</th>
                  <th className="px-2 py-1.5 text-left">Period</th>
                  <th className="px-2 py-1.5 text-right">Shares</th>
                  <th className="px-2 py-1.5 text-right">Market Value</th>
                  <th className="px-2 py-1.5 text-center">Doc</th>
                </tr>
              </thead>
              <tbody>
                {f13Rows.slice(0, 15).map((r, idx) => (
                  <F13Row key={idx} row={r} onOpenFiler={openFiler} primaryTicker={ticker} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </section>

      <p className="text-[10px] text-muted-foreground leading-snug">
        Data from SEC EDGAR via Massive. Filings disclosed within 2 business days; insider open-market activity is one signal among many.
        Always cross-reference with company fundamentals before acting on it.
      </p>

      {/* Filer drill-down — admin-only paywall proxy. Renders even
          when closed (mounted once, controlled via `open`) so the
          ESC-handler and fade animation stay smooth across rapid
          row clicks. */}
      <FilerHoldingsModal
        open={filerDrill.open}
        filerCik={filerDrill.cik}
        filerName={filerDrill.name}
        onClose={closeFiler}
      />
    </div>
  );
}

function TrendChip({ trend }) {
  const isBuilding = trend.direction === 'building';
  const delta = trend.delta;
  const absDelta = Math.abs(delta);
  const Icon = isBuilding ? ArrowUpRight : ArrowDownRight;
  const cls = isBuilding
    ? 'bg-emerald-600/90 text-white ring-emerald-400/50'
    : 'bg-rose-600/90 text-white ring-rose-400/50';
  const label = isBuilding ? 'BUILDING' : 'FADING';
  const prev = trend.previous_count;
  const snapDate = trend.previous_snapshot_at
    ? new Date(trend.previous_snapshot_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ring-1 ${cls}`}
      data-testid="smart-money-trend-chip"
      title={snapDate ? `Was ${prev} on ${snapDate} · now ${prev + delta}` : `Prior count: ${prev}`}
    >
      <Icon className="w-3 h-3" />
      {label} · {isBuilding ? '+' : '−'}{absDelta}
    </span>
  );
}

function SmartMoneyBadge({ summary, onOpenFiler }) {
  // Defensive — Mongo nullables / cache misses.
  if (!summary || !Array.isArray(summary.famous_holders) || summary.famous_holders.length === 0) {
    return null;
  }
  const count = summary.crossover_count;
  const tier = summary.tier; // "consensus" or "high-conviction"
  const isHigh = tier === 'high-conviction';
  const trend = summary.trend; // { delta, direction, previous_count, previous_snapshot_at, new_entrant_ciks }
  // Two-tier palette: amber for 3–4 holders (consensus), emerald
  // for 5+ (high-conviction). Both maintain AA contrast in light
  // & dark mode.
  const palette = isHigh
    ? {
        wrap: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700',
        text: 'text-emerald-800 dark:text-emerald-200',
        chip: 'bg-emerald-600 text-white',
        label: 'High-Conviction Crossover',
      }
    : {
        wrap: 'bg-amber-50 dark:bg-amber-950/25 border-amber-300 dark:border-amber-700',
        text: 'text-amber-900 dark:text-amber-200',
        chip: 'bg-amber-500 text-white',
        label: 'Smart-Money Crossover',
      };
  return (
    <div
      className={`mb-3 rounded-md border p-2.5 ${palette.wrap} ${palette.text}`}
      data-testid="smart-money-crossover"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${palette.chip}`}>
          <Target className="w-3 h-3" />
          {palette.label}
        </span>
        <span className="text-[11px] font-semibold">
          {count} famous {count === 1 ? 'fund holds' : 'funds hold'} this ticker
          {summary.total_smart_money_value > 0 ? (
            <> · {fmtMoney(summary.total_smart_money_value)} combined</>
          ) : null}
        </span>
        {/* Round P129b — quarter-over-quarter trend chip. Only renders
            when we have a prior snapshot ≥ 45 days old so first-touch
            tickers don't show a false-positive "new" arrow. */}
        {trend && trend.direction && trend.direction !== 'steady' && (
          <TrendChip trend={trend} />
        )}
      </div>
      {/* Chips — click any to drill into that filer's full holdings.
          Limit visible chips to 6 on phone so the badge stays tidy. */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {summary.famous_holders.slice(0, 8).map((h, idx) => {
          const clickable = !!(onOpenFiler && h.filer_cik);
          const inner = (
            <>
              <span className="font-semibold truncate max-w-[170px]" title={h.name}>{h.name}</span>
              {h.market_value > 0 && (
                <span className="text-[10px] opacity-80 font-mono">{fmtMoney(h.market_value)}</span>
              )}
            </>
          );
          return clickable ? (
            <button
              key={idx}
              type="button"
              onClick={() => onOpenFiler(h.filer_cik, h.name)}
              className="inline-flex items-center gap-1 text-[11px] bg-background/70 dark:bg-background/40 border border-current/30 rounded-full px-2 py-0.5 hover:bg-background transition-colors"
              data-testid={`smart-money-chip-${h.filer_cik}`}
              title={`See ${h.name}'s full portfolio`}
            >
              {inner}
            </button>
          ) : (
            <span
              key={idx}
              className="inline-flex items-center gap-1 text-[11px] bg-background/70 dark:bg-background/40 border border-current/30 rounded-full px-2 py-0.5"
            >
              {inner}
            </span>
          );
        })}
        {summary.famous_holders.length > 8 && (
          <span className="inline-flex items-center text-[11px] opacity-70 px-1">
            +{summary.famous_holders.length - 8} more in table below
          </span>
        )}
      </div>
    </div>
  );
}


function ClusterSummaryStrip({ summary }) {
  const buys = summary.buys || 0;
  const sells = summary.sells || 0;
  const net = summary.net_value || 0;
  const tag = summary.cluster_tag;
  const totalSignals = buys + sells;
  if (totalSignals === 0) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="insider-summary-strip">
      <Stat label="Buys (30d)" value={buys} tone="emerald" icon={TrendingUp} />
      <Stat label="Sells (30d)" value={sells} tone="rose" icon={TrendingDown} />
      <Stat
        label="Net $ (30d)"
        value={fmtMoney(net)}
        tone={net > 0 ? 'emerald' : net < 0 ? 'rose' : 'slate'}
      />
      {tag === 'cluster-buy' && (
        <div
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-emerald-400/50 bg-emerald-50 dark:bg-emerald-950/40 text-[11px] font-bold text-emerald-800 dark:text-emerald-200"
          data-testid="insider-cluster-tag-buy"
        >
          <Crown className="w-3.5 h-3.5" />
          <div className="leading-tight">
            <div>Cluster Buy</div>
            <div className="text-[9px] font-normal text-emerald-700/80 dark:text-emerald-300/70">
              {summary.unique_buyers}+ insiders buying
            </div>
          </div>
        </div>
      )}
      {tag === 'cluster-sell' && (
        <div
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-rose-400/50 bg-rose-50 dark:bg-rose-950/40 text-[11px] font-bold text-rose-800 dark:text-rose-200"
          data-testid="insider-cluster-tag-sell"
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          <div className="leading-tight">
            <div>Cluster Sell</div>
            <div className="text-[9px] font-normal text-rose-700/80 dark:text-rose-300/70">
              {summary.unique_sellers}+ insiders selling
            </div>
          </div>
        </div>
      )}
      {!tag && totalSignals > 0 && (
        <div
          className="flex items-center justify-center px-2 py-1.5 rounded-md border border-dashed border-border text-[10px] text-muted-foreground"
          data-testid="insider-no-cluster"
        >
          No cluster signal
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone, icon: Icon }) {
  const toneCls =
    tone === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' :
    tone === 'rose'    ? 'text-rose-600 dark:text-rose-400' :
    tone === 'amber'   ? 'text-amber-600 dark:text-amber-400' :
                         'text-foreground';
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-border bg-card">
      {Icon && <Icon className={`w-3.5 h-3.5 ${toneCls}`} />}
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`text-sm font-mono font-bold ${toneCls}`}>{value}</div>
      </div>
    </div>
  );
}

// Round 97l (Feb 2026) — Initial Insider Positions (SEC Form 3).
// Shows the BASELINE ownership of every new insider at the moment
// they first became subject to Section-16 reporting. Companion to
// the Form 4 timeline: Form 3 is the dot, Form 4 is every move.
//
// Design choices:
//  • Sorted by shares_owned desc on the backend so the biggest
//    baseline positions surface first (the dots that matter most).
//  • Direct vs Indirect ownership shown as a small chip — indirect
//    holdings (via family trusts, LLCs) often signal long-term
//    estate-planning intent rather than tactical entry.
//  • Capped at 10 visible rows with an expandable More toggle so
//    legacy mega-caps with 30+ insiders don't blow up the modal.
function Form3Section({ rows, ticker, user, sectionRef }) {
  const [expanded, setExpanded] = useState(false);
  const visibleRows = expanded ? rows : rows.slice(0, 10);
  return (
    <section data-testid="insider-form3-section">
      <header className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
        <h3 className="text-sm font-bold flex items-center gap-1.5">
          <Flag className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />
          Initial Insider Positions
        </h3>
        <div className="flex items-baseline gap-3 flex-wrap">
          <SectionActions
            targetRef={sectionRef}
            elementId={`insider-form3-${ticker}`}
            title={`${ticker} — Initial Insider Positions`}
            filename={`${ticker}-insider-baseline-form3`}
            user={user}
            testid="insider-form3-actions"
          />
          <span className="text-[10px] text-muted-foreground">
            SEC Form 3 · Section-16 baseline
          </span>
        </div>
      </header>
      <div ref={sectionRef} id={`insider-form3-${ticker}`} data-pdf-root>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="min-w-full text-[11px]" data-testid="insider-form3-table">
            <thead className="bg-muted/40">
              <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="px-2 py-1.5 text-left">Filing Date</th>
                <th className="px-2 py-1.5 text-left">Insider</th>
                <th className="px-2 py-1.5 text-left">Security</th>
                <th className="px-2 py-1.5 text-center">Ownership</th>
                <th className="px-2 py-1.5 text-right">Shares Owned</th>
                <th className="px-2 py-1.5 text-center">Doc</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r, idx) => (
                <Form3Row key={`${r.owner_name}-${r.security_title}-${idx}`} row={r} />
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > 10 && (
          <div className="mt-2 text-center">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-[11px] text-muted-foreground hover:text-foreground underline decoration-dotted underline-offset-2"
              data-testid="insider-form3-toggle"
            >
              {expanded
                ? 'Show fewer'
                : `Show all ${rows.length} baseline positions`}
            </button>
          </div>
        )}
        <p className="mt-2 text-[10px] text-muted-foreground leading-snug">
          Form 3 captures an insider&apos;s starting holdings at the moment they joined the board, became an officer, or crossed the 10% threshold. Use it to spot under-the-radar high-conviction stakes that pre-date any Form 4 trade.
        </p>
      </div>
    </section>
  );
}

function Form3Row({ row }) {
  const owned = row.shares_owned;
  const ownership = (row.direct_or_indirect || '').toUpperCase();
  // Direct = held in own name. Indirect = held via trust, family,
  // partnership, etc. The latter often signals estate-planning or
  // long-tenure stakes rather than tactical entry.
  const isDirect = ownership === 'D';
  const isIndirect = ownership === 'I';
  const ownerLabel = isDirect ? 'Direct' : isIndirect ? 'Indirect' : (row.direct_or_indirect || '—');
  const chipCls = isDirect
    ? 'bg-sky-100 dark:bg-sky-900/40 ring-sky-300/60 text-sky-900 dark:text-sky-200'
    : isIndirect
    ? 'bg-violet-100 dark:bg-violet-900/40 ring-violet-300/60 text-violet-900 dark:text-violet-200'
    : 'bg-muted ring-border text-muted-foreground';
  return (
    <tr className="border-t border-border/40 hover:bg-muted/20">
      <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">
        {fmtDate(row.filing_date)}
      </td>
      <td className="px-2 py-1.5">
        <div className="font-semibold text-foreground leading-tight">{row.owner_name || '—'}</div>
        <div className="text-[10px] text-muted-foreground leading-tight">{roleLabel(row)}</div>
      </td>
      <td className="px-2 py-1.5 text-muted-foreground">
        <div className="leading-tight">{row.security_title || 'Common Stock'}</div>
        {row.nature_of_ownership && (
          <div className="text-[10px] opacity-70 leading-tight truncate max-w-[180px]" title={row.nature_of_ownership}>
            {row.nature_of_ownership}
          </div>
        )}
      </td>
      <td className="px-2 py-1.5 text-center">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full ring-1 text-[10px] font-bold ${chipCls}`}>
          {ownerLabel}
        </span>
      </td>
      <td className="px-2 py-1.5 text-right font-mono font-bold text-foreground">
        {fmtShares(owned)}
      </td>
      <td className="px-2 py-1.5 text-center">
        {row.filing_url ? (
          <a
            href={row.filing_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-muted-foreground hover:text-foreground"
            title="Open SEC Form 3 filing in new tab"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : '—'}
      </td>
    </tr>
  );
}

function Form4Row({ row }) {
  const code = (row.transaction_code || '').toUpperCase();
  const meta = TRANSACTION_CODES[code] || { label: code || 'Other', tone: 'slate' };
  const tone = meta.tone;
  const toneCls =
    tone === 'emerald' ? 'text-emerald-700 dark:text-emerald-300' :
    tone === 'rose'    ? 'text-rose-700 dark:text-rose-300' :
    tone === 'amber'   ? 'text-amber-700 dark:text-amber-300' :
                         'text-muted-foreground';
  // Round 93p (Jun 2026) — the pill chips ride on a coloured
  // background so they need a noticeably DARKER text colour than
  // the surrounding row to stay readable (especially in the PDF
  // export, where dark-mode variants get stripped by the print
  // body class). Bumped from -700/-300 to -900/-200.
  const tonePillText =
    tone === 'emerald' ? 'text-emerald-900 dark:text-emerald-200' :
    tone === 'rose'    ? 'text-rose-900 dark:text-rose-200' :
    tone === 'amber'   ? 'text-amber-900 dark:text-amber-200' :
                         'text-foreground';
  const tonePill =
    tone === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/40 ring-emerald-300/60' :
    tone === 'rose'    ? 'bg-rose-100 dark:bg-rose-900/40 ring-rose-300/60' :
    tone === 'amber'   ? 'bg-amber-100 dark:bg-amber-900/40 ring-amber-300/60' :
                         'bg-muted ring-border';
  return (
    <tr className="border-t border-border/40 hover:bg-muted/20">
      <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">
        {fmtDate(row.filing_date)}
      </td>
      <td className="px-2 py-1.5">
        <div className="font-semibold text-foreground leading-tight">{row.owner_name || '—'}</div>
        <div className="text-[10px] text-muted-foreground leading-tight">{roleLabel(row)}</div>
      </td>
      <td className="px-2 py-1.5">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ring-1 text-[10px] font-bold ${tonePill} ${tonePillText}`}>
          {meta.label}
        </span>
      </td>
      <td className={`px-2 py-1.5 text-right font-mono ${toneCls}`}>{fmtShares(row.shares)}</td>
      <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">
        {row.price_per_share != null ? `$${Number(row.price_per_share).toFixed(2)}` : '—'}
      </td>
      <td className={`px-2 py-1.5 text-right font-mono font-bold ${toneCls}`}>
        {fmtMoney(row.transaction_value)}
      </td>
      <td className="px-2 py-1.5 text-center text-muted-foreground">
        {row.aff_10b5_one ? <span title="Filed under a 10b5-1 trading plan">✓</span> : '—'}
      </td>
      <td className="px-2 py-1.5 text-center">
        {row.filing_url ? (
          <a
            href={row.filing_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-muted-foreground hover:text-foreground"
            title="Open SEC filing in new tab"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : '—'}
      </td>
    </tr>
  );
}

function F13Row({ row, onOpenFiler, primaryTicker = null }) {
  const displayName = row.filer_name || (row.other_managers && row.other_managers[0]) || row.filer_cik || '—';
  // Only enable the drill-down click when we actually have a CIK to
  // query against. Otherwise the name renders as plain text.
  const clickable = !!(onOpenFiler && row.filer_cik);
  // Round P129 — dual-class family label. When a row was hydrated
  // from a sibling class bucket (see server _family_class_ticker
  // tag), show a small chip so the reader knows this holding is
  // in the OTHER class of the same issuer.
  const siblingClassTicker = row._family_class_ticker && row._family_class_ticker !== primaryTicker
    ? row._family_class_ticker
    : null;
  return (
    <tr className="border-t border-border/40 hover:bg-muted/20">
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1.5">
          {clickable ? (
            <button
              type="button"
              onClick={() => onOpenFiler(row.filer_cik, displayName)}
              // Round 92o++++++++N++++++++++ — clickable filer drills
              // into a modal showing that manager's top 25 holdings
              // (latest 13-F quarter). The dotted underline + hover
              // ring matches the rest of the app's inline-link style
              // (See AbbrTip / InfoRow link affordances).
              className="font-semibold text-foreground text-left hover:text-violet-600 dark:hover:text-violet-300 underline decoration-dotted decoration-muted-foreground/40 hover:decoration-violet-500 underline-offset-2 transition-colors"
              title="Click to see this filer's other holdings"
              data-testid={`filer-drill-${row.filer_cik}`}
            >
              {displayName}
            </button>
          ) : (
            <span className="font-semibold text-foreground">
              {displayName}
            </span>
          )}
          {row.is_famous && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 ring-1 ring-amber-300/60 text-[9px] font-bold text-amber-800 dark:text-amber-200"
              title="Famous institutional investor"
            >
              <Crown className="w-2.5 h-2.5 mr-0.5" />
              FAMOUS
            </span>
          )}
          {siblingClassTicker && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 ring-1 ring-violet-300/60 text-[9px] font-mono text-violet-800 dark:text-violet-200"
              title={`Filed under sibling class ${siblingClassTicker}`}
            >
              via {siblingClassTicker}
            </span>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground">{row.title_of_class || ''}</div>
      </td>
      <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{fmtDate(row.period)}</td>
      <td className="px-2 py-1.5 text-right font-mono">{fmtShares(row.shares)}</td>
      <td className="px-2 py-1.5 text-right font-mono font-bold">{fmtMoney(row.market_value)}</td>
      <td className="px-2 py-1.5 text-center">
        {row.filing_url ? (
          <a
            href={row.filing_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-muted-foreground hover:text-foreground"
            title="Open SEC 13-F filing in new tab"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : '—'}
      </td>
    </tr>
  );
}
